import {
  type UnitOfMeasure,
  formatPointsForDisplay,
  parseValueWithUnit,
} from '@/lib/units/unit-conversion'

const MANUAL_SPACING_PRESET_POINTS = [0, 4, 8, 16, 24]

export type SpacingOption = { label: string; value: number }

export function normalizeSpacingPoints(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }

  return Math.max(0, value)
}

export function buildSpacingOptions(
  unitOfMeasure: UnitOfMeasure,
  spacingUnitSuffix: string
): SpacingOption[] {
  return MANUAL_SPACING_PRESET_POINTS.map((value) => ({
    label:
      value === 0
        ? 'None'
        : `${formatPointsForDisplay(value, unitOfMeasure, { maxDecimals: 4 })}${spacingUnitSuffix}`,
    value,
  }))
}

/** The label of the preset matching `value`, or null when no preset matches. */
export function findSpacingPresetLabel(options: SpacingOption[], value: number): string | null {
  const matchingOption = options.find((option) => option.value === value)

  return matchingOption ? matchingOption.label : null
}

/** Resolves typed spacing text to points: blank, unparseable or negative falls back to the first
 *  preset. `decimalPlaces` is only set when the text parsed. */
export function resolveSpacingInput(
  raw: string,
  unitOfMeasure: UnitOfMeasure,
  options: SpacingOption[]
): { points: number; decimalPlaces?: number } {
  const fallback = options[0]?.value ?? 0

  if (raw === '') {
    return { points: fallback }
  }

  const parsed = parseValueWithUnit(raw, unitOfMeasure)
  const points =
    parsed.error || parsed.points < 0 ? fallback : normalizeSpacingPoints(parsed.points)

  return { points, decimalPlaces: parsed.decimalPlaces }
}
